import { SimulatorType } from "@/types/api/SimulatorType"
import type { SimulationMetadata } from "@/types/backend-contracts"
import { getPairedOpenAIRealtimeVoice } from "@/utils/simulation/simulatorFields"

type MetadataRecord = Record<string, unknown>

function getString(metadata: MetadataRecord, key: string): string | undefined {
  const value = metadata[key]
  return typeof value === "string" ? value : undefined
}

/**
 * MODEL_TYPE_OUTBOUND_VOICE persists its webhook URL in the agent's top-level
 * `endpoint` field, which the backend then syncs to `metadata.trigger_call_endpoint`
 * (the key the simulator reads). The edit form only writes `trigger_call_endpoint`
 * into metadata, so without mirroring it back onto the top-level `endpoint` the URL
 * is silently dropped on save. Given a metadata update, return the endpoint value to
 * mirror onto `formData.endpoint`, or `undefined` when it does not apply (non-outbound
 * agent, or no `trigger_call_endpoint` present in the update).
 */
export function getOutboundEndpointSync(
  simulatorType: SimulatorType,
  metadataUpdate: MetadataRecord,
): string | undefined {
  if (simulatorType !== SimulatorType.OUTBOUND_VOICE) {
    return undefined
  }
  return getString(metadataUpdate, "trigger_call_endpoint")
}

/**
 * Read-side counterpart to {@link getOutboundEndpointSync}. The outbound-voice
 * form field reads `metadata.trigger_call_endpoint`, but that key is backend-derived
 * and is not reliably round-tripped through a save (the top-level `endpoint` is the
 * source of truth that persists). Returns a metadata patch that surfaces the persisted
 * top-level `endpoint` as `trigger_call_endpoint` so the field reflects the saved value
 * on reload, or `undefined` when it does not apply (non-outbound, or no endpoint set).
 */
export function getOutboundEndpointDisplay(
  simulatorType: SimulatorType,
  endpoint: string | undefined,
): { trigger_call_endpoint: string } | undefined {
  if (simulatorType !== SimulatorType.OUTBOUND_VOICE || !endpoint) {
    return undefined
  }
  return { trigger_call_endpoint: endpoint }
}

export function getSimulatorMetadataString(
  metadata: MetadataRecord,
  key: string,
  fallback = "",
): string {
  return getString(metadata, key) ?? fallback
}

export function getPhoneNumberIndex(
  metadata: MetadataRecord,
): number | undefined {
  const value = metadata.phone_number_index
  if (typeof value === "number") {
    return Number.isInteger(value) && value >= 0 ? value : undefined
  }
  if (typeof value === "string") {
    const normalized = value.trim()
    if (!/^\d+$/.test(normalized)) {
      return undefined
    }
    const parsed = Number.parseInt(normalized, 10)
    return Number.isInteger(parsed) ? parsed : undefined
  }
  return undefined
}

export function createPhoneNumberIndexUpdate(value: number): MetadataRecord {
  if (!Number.isFinite(value) || !Number.isInteger(value) || value < 0) {
    throw new TypeError(
      `phone_number_index must be a non-negative integer, received ${value}`,
    )
  }
  return { phone_number_index: value.toString() }
}

export function createSimulatorStringUpdate(
  key: string,
  value: string,
): MetadataRecord {
  return { [key]: value }
}

export function createSttProviderUpdate(provider: string): MetadataRecord {
  return {
    provider,
    model: provider === "openai" ? "whisper-1" : "",
  }
}

export function createOpenAIRealtimeModelUpdate(
  model: string,
  metadata: MetadataRecord,
  disableWebSearch?: boolean,
): Partial<SimulationMetadata> {
  const currentModel =
    typeof metadata.openai_realtime_model === "string"
      ? metadata.openai_realtime_model
      : undefined
  const currentVoice =
    typeof metadata.openai_realtime_voice === "string"
      ? metadata.openai_realtime_voice
      : undefined
  const pairedVoice = getPairedOpenAIRealtimeVoice(
    model,
    currentModel,
    currentVoice,
  )
  return {
    openai_realtime_model: model,
    ...(disableWebSearch && {
      openai_realtime_web_search_enabled: false,
    }),
    ...(pairedVoice !== undefined && {
      openai_realtime_voice: pairedVoice,
    }),
  }
}
